import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Product } from '../../../stores/appReportsStore'
import { calculateTotalSpent } from '../../../services/import/importProductIdsService'
import { currencyFormatter } from '../../../services/helperService'
import { editOptionProductCardClass } from '../../../styling/stylingHelper'
import { SupplierLogo } from '../../ui/SupplierLogo'

interface SummaryLeftProductCardProps {
  product: Product
}

export default function SummaryLeftProductCard({ product }: SummaryLeftProductCardProps) {
  const { t } = useTranslation()
  const [imgSrc, setImgSrc] = useState(product.src)

  const totalPrice = calculateTotalSpent(product)
  const unitInfo   = `${currencyFormatter.format(product.unitPrice)}/${product.unitPriceUnit}`

  const stdAmt     = product.useCombo
    ? product.combo?.standardizedAmount ?? product.standardizedAmount
    : product.standardizedAmount
  const isGrMl     = ['g', 'ml'].includes(product.standardUnit)
  const totalQty   = isGrMl ? (product.quantity * stdAmt) / 1000 : product.quantity * stdAmt
  const qty        = new Intl.NumberFormat(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(totalQty)
  const stdUnit    = product.standardUnit === 'g' ? 'kg' : product.standardUnit === 'ml' ? 'ltr' : product.standardUnit

  return (
    <div className={`${editOptionProductCardClass} flex flex-col md:flex-row p-3 w-full`}>
      {/* top: image + info stay side-by-side on mobile */}
      <div className="flex w-full">
        {/* image */}
        <div className="border border-borderLightGrey dark:border-borderLightGrey-dark rounded-xl">
          <img
            src={imgSrc}
            alt={product.name}
            className="w-[70px] h-[70px] object-cover rounded-xl border-2 border-white dark:border-card-dark"
            onError={e => {
              e.currentTarget.onerror = null
              setImgSrc('/no_photo.png')
            }}
          />
        </div>

        {/* info */}
        <div className="flex-1 flex flex-col justify-between ml-3 min-w-0">
          {/* qty / name (+ mobile article id) + desktop price/total */}
          <div className="flex items-stretch justify-between">
            {/* qty + name */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-start">
                <div className="w-fit text-xs font-medium rounded-lg bg-textBlue/10 dark:bg-textBlue-dark/10 px-2 py-0.5 text-textBlue dark:text-textBlue-dark mb-1">
                  {qty}{stdUnit}
                </div>
                <div className="w-fit text-xs font-medium rounded-lg bg-borderGrey dark:bg-borderGrey-dark px-2 py-0.5 text-tertiary dark:text-tertiary-dark mb-1 block md:hidden ml-2">
                  ID: {product.articleId}
                </div>
              </div>
              <a
                href={product.link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-text dark:text-text-dark"
              >
                <h4 className="truncate font-semibold" title={product.name}>
                  {product.name}
                </h4>
              </a>
            </div>

            {/* desktop-only dividers + unit price */}
            <div className="hidden md:flex self-center w-px h-[60%] bg-borderGrey dark:bg-borderGrey-dark my-1 mx-1.5" />
            <div className="hidden md:flex flex-col px-2">
              <span className="text-base text-muted dark:text-muted-dark">{t('product.price')}</span>
              <span className="text-primary dark:text-primary-dark font-semibold truncate">{unitInfo}</span>
            </div>

            {/* desktop-only divider + total */}
            <div className="hidden md:flex self-center w-px h-[60%] bg-borderGrey dark:bg-borderGrey-dark my-1 mx-1.5" />
            <div className="hidden md:flex items-end px-2">
              <span className="text-base text-primary dark:text-primary-dark font-bold">
                {currencyFormatter.format(Number(totalPrice))}
              </span>
            </div>
          </div>

          {/* desktop-only bottom brand row */}
          <div className="flex items-center mt-2">
            <span className="text-muted dark:text-muted-dark mr-1">{t('product.brand')}</span>
            <span className="text-secondary dark:text-secondary-dark font-medium">{product.brand}</span>
            <span className="text-muted dark:text-muted-dark mx-1 font-semibold hidden md:block">·</span>
            <span className="text-sm text-muted dark:text-muted-dark font-medium hidden md:block">
              {product.articleId}
            </span>
            <span className="text-muted dark:text-muted-dark mx-1 font-semibold">·</span>
            <SupplierLogo
              platform={product.platform}
              alt={product.platform}
              size="small"
              className="inline-block align-middle"
            />
          </div>
        </div>
      </div>

      {/* mobile-only unit price / total row */}
      <div className="flex justify-between mt-2 md:hidden px-2">
        <div className="flex flex-col">
          <span className="text-sm text-muted dark:text-muted-dark">{t('product.price')}</span>
          <span className="font-semibold">{unitInfo}</span>
        </div>
        <span className="text-base text-primary dark:text-primary-dark font-bold">
          {currencyFormatter.format(Number(totalPrice))}
        </span>
      </div>
    </div>
  )
}
